import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { colors } from 'themes/tokens';

import { CollapseSidepanel } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  primaryDrawerWidth,
  secondaryDrawerWidth,
} from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SecondaryPanelAction } from '../types/FeedbackSlice';
import { Footer } from './Footer';
import GoalSelectionFooter from './GoalSelection/Footer';
import GoalSelection from './GoalSelection/GoalSelection';
import MeshMavenFooter from './MeshMaven/Footer';
import MeshMaven from './MeshMaven/MeshMaven';

interface Props {
  onClose: () => void;
  header: React.ReactNode;
  children: React.ReactNode;
  onSave: () => void;
  showSecondaryPanel?: boolean;
  secondaryPanelAction?: SecondaryPanelAction;
  selectedId?: string;
  onSelect?: (goal: Record<string, any>) => void;
  secondaryPanelHeader?: React.ReactNode;
  onSecondaryPanelClose?: () => void;
  applicableEmployees?: string[];
  saveButtonDisabled?: boolean;
  saveButtonDisabledReason?: string;
}

const StyledBody = styled(DrawerBody)`
  width: 100%;
  overflow: auto;
  flex: 1;
  display: flex;
`;

const StyledCollapseSidepanel = styled(CollapseSidepanel)`
  color: #666666 !important;
`;

const Layout = ({
  onClose,
  header,
  children,
  onSave,
  showSecondaryPanel = false,
  secondaryPanelAction = 'GOAL_SELECTION',
  onSelect,
  selectedId,
  secondaryPanelHeader = '',
  onSecondaryPanelClose,
  applicableEmployees,
  saveButtonDisabled = false,
  saveButtonDisabledReason = '',
}: Props) => {
  const showOnMobileView = !(showSecondaryPanel && isMobileView());

  return (
    <DrawerContent
      sx={{
        padding: '4px !important',
      }}
      width={{
        base: '100%',
        md: showSecondaryPanel ? `${primaryDrawerWidth + secondaryDrawerWidth}px` : `${primaryDrawerWidth}px`,
      }}
      mr={{
        base: '0 !important',
        md: '32px !important',
      }}
    >
      <Box
        sx={{
          borderRadius: '8px',
          p: 0,
          '.close-button-drawer': {
            base: {
              top: '-28px',
              left: '-14px',
            },
            md: {
              top: '-28px',
              left: '-28px',
            },
          },
          height: '100%',
          display: 'flex',
          width: '100%',
          bg:
            !showSecondaryPanel || secondaryPanelAction === 'GOAL_SELECTION'
              ? colors['#EBEFF3']
              : 'linear-gradient(90deg, #EBEFF3 50%, rgba(0, 142, 240, 0.20) 51%, rgba(236, 59, 100, 0.20) 67.43%, rgba(255, 194, 0, 0.20) 82.77%, rgba(92, 196, 97, 0.20) 100%)',
        }}
        className="drawer-content-wrapper"
      >
        <Box
          display="flex"
          flexDir="column"
          w={{
            base: showSecondaryPanel ? 0 : '100%',
            md: '100%',
          }}
        >
          {' '}
          <DrawerHeader onClose={onClose} opacity={1} background="white">
            {showOnMobileView && (
              <Box
                p="12px"
                pb="8px !important"
                opacity={showSecondaryPanel ? 0.5 : 1}
                pointerEvents={showSecondaryPanel ? 'none' : 'unset'}
              >
                <TextBodyMedium bold>{header}</TextBodyMedium>
              </Box>
            )}
          </DrawerHeader>
          <Box
            className="first-drawer-wrapper"
            height="calc(100% - 40px)"
            w={{
              base: showSecondaryPanel ? 0 : '100%',
              md: '100%',
            }}
            padding="4px"
            borderRadius="8px"
            background="#EBEFF3"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 72px)',
                background: showSecondaryPanel ? '#FAFAFA' : '#FFFFFF',
                borderRadius: '8px',
              }}
              className="drawer-body-wrapper"
            >
              {showOnMobileView && (
                <StyledBody
                  opacity={showSecondaryPanel ? 0.5 : 1}
                  pointerEvents={showSecondaryPanel ? 'none' : 'unset'}
                >
                  {/* @ts-ignore */}
                  <Flex w="100%">{children}</Flex>
                </StyledBody>
              )}
            </Box>
            {showOnMobileView && (
              <Footer
                onSave={onSave}
                onClose={onClose}
                showSecondaryPanel={showSecondaryPanel}
                saveButtonDisabled={saveButtonDisabled}
                saveButtonDisabledReason={saveButtonDisabledReason}
              />
            )}
          </Box>
        </Box>

        {showSecondaryPanel && (
          <Box
            className="second-drawer-wrapper"
            height="100%"
            w="100%"
            overflow="hidden"
            boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)"
            bgColor="white"
          >
            <Box
              sx={{
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 72px)',
              }}
              className="drawer-body-wrapper"
            >
              <Box
                display="flex"
                w="100%"
                p="12px"
                pb="8px !important"
                alignItems="center"
                sx={{
                  button: {
                    height: '20px',
                    width: '20px',
                  },
                }}
              >
                <TextBodyMedium bold mr="auto">
                  {secondaryPanelHeader}
                </TextBodyMedium>
                <Button StartIcon={StyledCollapseSidepanel} variant="tertiary" onClick={onSecondaryPanelClose} />
              </Box>
              <Box h="100%" borderRadius="0px 8px 8px 0px" overflow="hidden">
                {secondaryPanelAction === 'GOAL_SELECTION' ? (
                  <GoalSelection
                    applicableEmployees={applicableEmployees}
                    onSelect={onSelect}
                    selectedId={selectedId}
                  />
                ) : (
                  <MeshMaven />
                )}
              </Box>
            </Box>
            <DrawerFooter h="72px" backgroundColor="neutral.F5F7F9">
              {secondaryPanelAction === 'GOAL_SELECTION' ? (
                <GoalSelectionFooter
                  onCloseSecondaryPanel={onSecondaryPanelClose}
                  onGoalSelect={onSecondaryPanelClose}
                />
              ) : (
                <MeshMavenFooter onCloseSecondaryPanel={onSecondaryPanelClose} />
              )}
            </DrawerFooter>
          </Box>
        )}
      </Box>
    </DrawerContent>
  );
};

export default Layout;
